import { useState, useEffect } from 'react';
import { 
  TrendingUp, Package, Calendar, Users,
  Filter, RefreshCw, Download, PieChart, BarChart3, Plane,
  CheckCircle, XCircle, DollarSign, ShoppingCart} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import api from '../../api/axios';
import ReusableTable from '../../components/Tableau';

const SalesStatusPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  
  const [loading, setLoading] = useState(true);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);
  
  const [data, setData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [providers, setProviders] = useState([]);
  
  // État pour le modal de détails
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  const [stats, setStats] = useState({
    totalProducts: 0,
    confirmedBookings: 0,
    cancelledBookings: 0,
    pendingBookings: 0,
    uniqueServices: 0,
    uniqueProviders: 0
  });
  
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    service: '',
    country_id: '',
    provider_id: ''
  });
  
  const services = ['hotel', 'flight', 'transfer', 'activity', 'package'];

  // Fonction pour charger TOUS les pays avec pagination
  const fetchAllCountries = async () => {
    setCountriesLoading(true);
    try {
      const firstResponse = await api.get(process.env.REACT_APP_API_COUNTRY_URL);
      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;
      
      let allCountries = [...firstData.data];
      
      if (lastPage > 1) {
        const promises = [];
        
        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(process.env.REACT_APP_API_COUNTRY_URL, {
              params: { page }
            }).then(res => res.data.data)
          );
        }
        
        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allCountries.push(...pageData);
        });
      }
      
      const formattedCountries = allCountries.map(country => ({
        id: country.id,
        name: country[lang] || country.en || country.fr || country.es || country.id,
        code: country.id
      })).sort((a, b) => a.name.localeCompare(b.name));
      
      setCountries(formattedCountries);
      
    } catch (error) {
      console.error('Erreur lors du chargement des pays:', error);
      toast.error(t('errorLoadingCountries'));
    } finally {
      setCountriesLoading(false);
    }
  };

  // charger tous les providers avec pagination
  const fetchAllProviders = async () => {
    setProvidersLoading(true);
    try {
      const urlBase = process.env.REACT_APP_API_PROVIDER_URL;
          const baseRequestBody = {
      search: "",
      status: ""
    };

          const firstResponse = await api.post(urlBase, baseRequestBody, {
      params: { page: 1 } 
    });
        
      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;
      
      let allProviders = [...firstData.result.data];
      
      if (lastPage > 1) {
        const promises = [];
        
        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(urlBase, baseRequestBody, {
              params: { page: page }
            }).then(res => res.data.result.data)
          );
        }
        
        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allProviders.push(...pageData);
        });
      }
      
      const formattedProviders = allProviders.map(provider => ({
        id: provider.id,
        name: provider.name,
        email: provider.email,
        phone: provider.phone,
        country_id: provider.country_id,
        country_name: provider.country?.[lang] || provider.country?.fr || provider.country?.id
      })).sort((a, b) => a.name.localeCompare(b.name));
      
      setProviders(formattedProviders);
      
    } catch (error) {
      console.error('Erreur lors du chargement des providers:', error);
      toast.error(t('errorLoadingProviders'));
    } finally {
      setProvidersLoading(false);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    fetchSalesData();
    fetchAllCountries();
    fetchAllProviders();
  }, []);

  // charger les données de vente
  const fetchSalesData = async (filterParams = {}) => {
    setLoading(true);
    try {
      const response = await api.post(process.env.REACT_APP_API_SALES_STATUS, filterParams);
      
      if (response.data.request?.status === true) {
        setData(response.data.result || []);
        calculateStats(response.data.result);
      } else {
        toast.error(t('errorLoadingData'));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error(t('errorLoadingData'));
    } finally {
      setLoading(false);
    }
  };

  // Calculer les nouvelles statistiques
  const calculateStats = (salesData) => {
    if (!salesData || salesData.length === 0) {
      setStats({
        totalProducts: 0,
        confirmedBookings: 0,
        cancelledBookings: 0,
        pendingBookings: 0,
        uniqueServices: 0,
        uniqueProviders: 0
      });
      return;
    }

    let totalProducts = 0;
    let confirmedBookings = 0;
    let cancelledBookings = 0;
    let pendingBookings = 0;
    const uniqueServices = new Set();
    const uniqueProviders = new Set();

    salesData.forEach(item => {
      totalProducts += item.total_bookings || 0;
      uniqueServices.add(item.service);
      uniqueProviders.add(item.provider_name);
      
      switch (item.status) {
        case 'confirmed':
          confirmedBookings += item.total_bookings || 0;
          break;
        case 'cancelled':
          cancelledBookings += item.total_bookings || 0;
          break;
        case 'pending':
          pendingBookings += item.total_bookings || 0;
          break;
      }
    });

    setStats({
      totalProducts,
      confirmedBookings,
      cancelledBookings,
      pendingBookings,
      uniqueServices: uniqueServices.size,
      uniqueProviders: uniqueProviders.size
    });
  };

  // changements de filtres
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Appliquer les filtres
  const applyFilters = () => {
    const filterParams = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '')
    );
    
    fetchSalesData(filterParams);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      service: '',
      country_id: '',
      provider_id: ''
    });
    fetchSalesData();
  };

  const exportToExcel = () => {
    if (!data || data.length === 0) {
      toast.error(t('noDataToExport'));
      return;
    }

    try {
      // Préparer les données pour Excel
      const excelData = data.map(item => ({
        [t('service')]: t(item.service),
        [t('status')]: t(item.status),
        [t('paymentStatus')]: t(item.payment_status),
        [t('totalBookings')]: item.total_bookings,
        [t('provider')]: item.provider_name,
        [t('providerRevenue')]: `${item.total_provider_revenue.toFixed(2)} ${item.provider_currency}`,
        [t('providerCurrency')]: item.provider_currency,
        [t('totalRevenue')]: `${item.total_revenue.toFixed(2)} ${item.currency}`,
        [t('currency')]: item.currency
      }));

      // Créer une feuille de calcul
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      const statsData = [
        { [t('statistics')]: '' },
        { [t('statistics')]: t('totalProducts'), '': stats.totalProducts },
        { [t('statistics')]: t('confirmedBookings'), '': stats.confirmedBookings },
        { [t('statistics')]: t('cancelledBookings'), '': stats.cancelledBookings },
        { [t('statistics')]: t('pendingBookings'), '': stats.pendingBookings },
        { [t('statistics')]: t('uniqueServices'), '': stats.uniqueServices },
        { [t('statistics')]: t('uniqueProviders'), '': stats.uniqueProviders },
        { [t('statistics')]: '' }, 
        { [t('statistics')]: t('filtersApplied'), '': '' },
        { [t('statistics')]: t('dateFrom'), '': filters.date_from || t('none') },
        { [t('statistics')]: t('dateTo'), '': filters.date_to || t('none') },
        { [t('statistics')]: t('service'), '': filters.service ? t(filters.service) : t('allServices') },
        { [t('statistics')]: t('provider'), '': filters.provider_id || t('allProviders') },
        { [t('statistics')]: t('country'), '': filters.country_id || t('allCountries') },
        { [t('statistics')]: '' }, 
      ];
      

      const wsStats = XLSX.utils.json_to_sheet(statsData, { skipHeader: true });
      
      // Créer un nouveau classeur avec plusieurs feuilles
      const wb = XLSX.utils.book_new();
      
      // Ajouter la feuille des données
      XLSX.utils.book_append_sheet(wb, ws, t('salesData'));
      
      // Ajouter la feuille des statistiques
      XLSX.utils.book_append_sheet(wb, wsStats, t('statistics'));
      
      // Mettre en forme les colonnes (largeur automatique)
      if (ws['!ref']) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        const wscols = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxLength = 0;
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
            if (cell && cell.v) {
              const cellLength = cell.v.toString().length;
              if (cellLength > maxLength) maxLength = cellLength;
            }
          }
          wscols.push({ wch: Math.min(maxLength + 2, 50) }); // Max 50 caractères
        }
        ws['!cols'] = wscols;
      }
      
      // Générer le nom du fichier avec la date
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0];
      const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
      const fileName = `sales-status-${dateStr}_${timeStr}.xlsx`;
      
      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);
      
      toast.success(t('exportSuccess'));
      
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      toast.error(t('exportError'));
    }
  };

  // Version alternative pour exporter en CSV
  const exportToCSV = () => {
    if (!data || data.length === 0) {
      toast.error(t('noDataToExport'));
      return;
    }

    try {
      // En-têtes
      const headers = [
        t('service'),
        t('status'),
        t('paymentStatus'),
        t('totalBookings'),
        t('provider'),
        t('providerRevenue'),
        t('providerCurrency'),
        t('totalRevenue'),
        t('currency')
      ];

      // Données
      const csvData = data.map(item => [
        t(item.service),
        t(item.status),
        t(item.payment_status),
        item.total_bookings,
        item.provider_name,
        item.total_provider_revenue.toFixed(2),
        item.provider_currency,
        item.total_revenue.toFixed(2),
        item.currency
      ]);

      // Créer le contenu CSV
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      // Créer un blob et télécharger
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `sales-status-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(t('exportCSVSuccess'));
      
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
      toast.error(t('exportError'));
    }
  };

// Fonction avec menu pour choisir le format
const exportData = () => {
  if (!data || data.length === 0) {
    toast.error(t('noDataToExport'));
    return;
  }

  // Vérifier si un menu est déjà ouvert
  const existingMenu = document.getElementById('export-menu');
  if (existingMenu) {
    document.body.removeChild(existingMenu);
    return;
  }

  // Créer le menu déroulant
  const exportMenu = document.createElement('div');
  exportMenu.id = 'export-menu';
  exportMenu.style.cssText = `
    position: fixed;
    background: white;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.15);
    padding: 8px 0;
    z-index: 9999;
    min-width: 220px;
    border: 1px solid #e5e7eb;
    animation: fadeIn 0.2s ease;
  `;
  
  // Ajouter l'animation CSS
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
  
  const formats = [
    { label: t('exportExcel'), action: exportToExcel },
    { label: t('exportCSV'), action: exportToCSV }
  ];
  
  formats.forEach(format => {
    const option = document.createElement('button');
    option.style.cssText = `
      width: 100%;
      padding: 12px 20px;
      background: none;
      border: none;
      text-align: left;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      display: flex;
      align-items: center;
      gap: 12px;
      transition: all 0.2s ease;
    `;
    option.innerHTML = `<span>${format.label}</span>`;
    
    option.onmouseenter = () => {
      option.style.background = '#f9fafb';
      option.style.color = '#7c3aed';
    };
    
    option.onmouseleave = () => {
      option.style.background = 'none';
      option.style.color = '#374151';
    };
    
    option.onclick = (e) => {
      e.stopPropagation();
      closeExportMenu();
      format.action();
    };
    
    exportMenu.appendChild(option);
  });
  
  // Ajouter une ligne séparatrice
  const separator = document.createElement('div');
  separator.style.cssText = `
    height: 1px;
    background: #e5e7eb;
    margin: 8px 0;
  `;
  exportMenu.appendChild(separator);
  
  //  "Copier les statistiques"
  const copyOption = document.createElement('button');
  copyOption.style.cssText = `
    width: 100%;
    padding: 12px 20px;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.2s ease;
  `;
  copyOption.innerHTML = '<span>Copier les statistiques</span>';
  
  copyOption.onmouseenter = () => {
    copyOption.style.background = '#f9fafb';
    copyOption.style.color = '#7c3aed';
  };
  
  copyOption.onmouseleave = () => {
    copyOption.style.background = 'none';
    copyOption.style.color = '#374151';
  };
  
  copyOption.onclick = (e) => {
    e.stopPropagation();
    copyStatsToClipboard();
    closeExportMenu();
  };
  
  exportMenu.appendChild(copyOption);
  
  // Fonction pour fermer le menu
  const closeExportMenu = () => {
    const menu = document.getElementById('export-menu');
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu);
    }
    if (style.parentNode === document.head) {
      document.head.removeChild(style);
    }
    document.removeEventListener('click', handleOutsideClick);
    window.removeEventListener('resize', closeExportMenu);
  };
  
  // Fonction pour copier les statistiques
  const copyStatsToClipboard = () => {
    const statsText = `
Statistiques des ventes:
=======================
Total des produits: ${stats.totalProducts}
Réservations confirmées: ${stats.confirmedBookings}
Réservations annulées: ${stats.cancelledBookings}
Services uniques: ${stats.uniqueServices}
Fournisseurs uniques: ${stats.uniqueProviders}

Date: ${new Date().toLocaleDateString()}
Filtres appliqués:
- Date de début: ${filters.date_from || 'Aucune'}
- Date de fin: ${filters.date_to || 'Aucune'}
- Service: ${filters.service ? t(filters.service) : 'Tous'}
- Fournisseur: ${filters.provider_id || 'Tous'}
- Pays: ${filters.country_id || 'Tous'}
    `.trim();
    
    navigator.clipboard.writeText(statsText).then(() => {
      toast.success('Statistiques copiées dans le presse-papier !');
    }).catch(() => {
      toast.error('Erreur lors de la copie');
    });
  };
  
  // Gérer le clic en dehors du menu
  const handleOutsideClick = (e) => {
    const menu = document.getElementById('export-menu');
    const exportButton = document.querySelector('[data-export-button]');
    
    if (menu && 
        !menu.contains(e.target) && 
        !exportButton?.contains(e.target)) {
      closeExportMenu();
    }
  };
  
  // Positionner le menu intelligemment
  const positionMenu = () => {
    const exportButton = document.querySelector('[data-export-button]');
    if (!exportButton) return;
    
    const rect = exportButton.getBoundingClientRect();
    const menuWidth = 220; // Largeur fixe du menu
    const menuHeight = exportMenu.offsetHeight;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Calculer la position par défaut (en dessous du bouton)
    let top = rect.bottom + window.scrollY + 5;
    let left = rect.left + window.scrollX;
    
    // Ajuster si le menu dépasse en bas
    if (top + menuHeight > windowHeight + window.scrollY) {
      top = rect.top + window.scrollY - menuHeight - 5;
    }
    
    // Ajuster si le menu dépasse à droite (bouton à l'extrémité droite)
    if (left + menuWidth > windowWidth) {
      left = rect.right + window.scrollX - menuWidth;
    }
    
    // Ajuster si le menu dépasse à gauche après ajustement
    if (left < 0) {
      left = 10; // Marge minimale
    }
    
    exportMenu.style.top = `${top}px`;
    exportMenu.style.left = `${left}px`;
  };
  
  document.body.appendChild(exportMenu);
  
  // Positionner après que le menu soit dans le DOM
  setTimeout(() => {
    positionMenu();
    
    // Réajuster si la fenêtre change de taille
    window.addEventListener('resize', closeExportMenu);
    
    // Ajouter l'écouteur pour les clics en dehors
    document.addEventListener('click', handleOutsideClick);
  }, 0);
};



  // Ouvrir le modal de détails
  const openDetailsModal = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  // Fermer le modal de détails
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedItem(null);
  };

  // Obtenir l'icône du service
  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'hotel': return <Package size={16} />;
      case 'flight': return <Plane size={16} />;
      case 'transfer': return <TrendingUp size={16} />;
      case 'activity': return <PieChart size={16} />;
      default: return <Package size={16} />;
    }
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#10b981';
      case 'cancelled': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  // Obtenir la couleur du statut de paiement
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#10b981';
      case 'unpaid': return '#ef4444';
      case 'partial': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  // Colonnes pour le tableau
  const columns = [
    {
      key: 'service',
      label: t('service'),
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getServiceIcon(value)}
          <span>{t(value)}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: t('status'),
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: getStatusColor(value)
        }}>
          {t(value)}
        </span>
      )
    },
    {
      key: 'payment_status',
      label: t('paymentStatus'),
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: getPaymentStatusColor(value)
        }}>
          {t(value)}
        </span>
      )
    },
    {
      key: 'total_bookings',
      label: t('totalBookings'),
      render: (value) => (
        <div style={{ fontWeight: '600', color: '#1f2937' }}>
          {value.toLocaleString()}
        </div>
      )
    },
    {
      key: 'provider_name',
      label: t('provider'),
      render: (value) => (
        <div style={{ color: '#6b7280' }}>
          {value}
        </div>
      )
    },
    {
      key: 'total_provider_revenue',
      label: t('providerRevenue'),
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937' }}>
            {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.provider_currency}
          </div>
        </div>
      )
    },
    {
      key: 'total_revenue',
      label: t('totalRevenue'),
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '600', color: '#7c3aed' }}>
            {value.toLocaleString()}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.currency}
          </div>
        </div>
      )
    }
  ];

  // Gestion du click sur une ligne
  const handleRowClick = (item) => {
    openDetailsModal(item);
  };

  // Styles
  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s ease',
      cursor: 'pointer'
    },
    statIconContainer: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    statInfo: {
      flex: 1
    },
    statLabel: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937'
    },
    statSubtext: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '4px'
    },
    
    // Modal styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: 'white',
      borderRadius: '16px',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      // overflowY: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    modalHeader: {
      padding: '24px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '8px'
    },
    modalBody: {
      padding: '24px'
    },
    detailGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '24px'
    },
    detailCard: {
      background: '#f8fafc',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #e5e7eb'
    },
    detailCardTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#6b7280',
      marginBottom: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    detailCardContent: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    detailBadge: {
      padding: '8px 16px',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      display: 'inline-block'
    },
    revenueSection: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '12px',
      padding: '24px',
      marginTop: '24px'
    },
    revenueTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1e40af',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    revenueGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px'
    },
    revenueItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    revenueLabel: {
      fontSize: '14px',
      color: '#6b7280'
    },
    revenueValue: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937'
    },
    revenueCurrency: {
      fontSize: '12px',
      color: '#9ca3af',
      marginLeft: '4px'
    },

    // Reste des styles existants...
    filtersContainer: {
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '32px'
    },
    filtersTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '20px',
      marginBottom: '24px'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    input: {
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      background: 'white',
      width: '100%'
    },
    select: {
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      background: 'white',
      width: '100%',
      cursor: 'pointer'
    },
    loadingSelect: {
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      background: '#f9fafb',
      width: '100%',
      color: '#9ca3af',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    filterButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    primaryButton: {
      padding: '10px 20px',
      background: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    secondaryButton: {
      padding: '10px 20px',
      background: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    exportButton: {
      padding: '10px 20px',
      background: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '80px 20px',
      color: '#6b7280'
    },
    noDataContainer: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#9ca3af',
      background: 'white',
      borderRadius: '12px'
    }
  };

  // Rendu du modal de détails
  const renderDetailsModal = () => {
    if (!selectedItem) return null;

    return (
      <div style={styles.modalOverlay} onClick={closeDetailsModal}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>
              {getServiceIcon(selectedItem.service)}
              {t('salesDetails')} - {t(selectedItem.service)}
            </h3>
            <button style={styles.closeButton} onClick={closeDetailsModal}>
              <XCircle size={24} />
            </button>
          </div>
          
          <div style={styles.modalBody}>
            {/* Informations principales */}
            <div style={styles.detailGrid}>
              <div style={styles.detailCard}>
                <div style={styles.detailCardTitle}>{t('status')}</div>
                <div style={{
                  ...styles.detailCardContent,
                  color: getStatusColor(selectedItem.status)
                }}>
                  {selectedItem.status === 'confirmed' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                  {t(selectedItem.status)}
                </div>
              </div>
              
              <div style={styles.detailCard}>
                <div style={styles.detailCardTitle}>{t('paymentStatus')}</div>
                <div style={styles.detailCardContent}>
                  <span style={{
                    ...styles.detailBadge,
                    backgroundColor: getPaymentStatusColor(selectedItem.payment_status)
                  }}>
                    {t(selectedItem.payment_status)}
                  </span>
                </div>
              </div>
              
              <div style={styles.detailCard}>
                <div style={styles.detailCardTitle}>{t('totalBookings')}</div>
                <div style={styles.detailCardContent}>
                  <Users size={20} />
                  {selectedItem.total_bookings.toLocaleString()}
                </div>
              </div>
              
              <div style={styles.detailCard}>
                <div style={styles.detailCardTitle}>{t('provider')}</div>
                <div style={styles.detailCardContent}>
                  <Package size={20} />
                  {selectedItem.provider_name}
                </div>
              </div>
            </div>
            
            {/* Section revenus */}
            <div style={styles.revenueSection}>
              <div style={styles.revenueTitle}>
                <DollarSign size={18} />
                {t('revenueDetails')}
              </div>
              
              <div style={styles.revenueGrid}>
                <div style={styles.revenueItem}>
                  <div style={styles.revenueLabel}>{t('providerRevenue')}</div>
                  <div style={styles.revenueValue}>
                    {selectedItem.total_provider_revenue.toLocaleString(undefined, { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                    <span style={styles.revenueCurrency}>{selectedItem.provider_currency}</span>
                  </div>
                </div>
                
                <div style={styles.revenueItem}>
                  <div style={styles.revenueLabel}>{t('totalRevenue')}</div>
                  <div style={styles.revenueValue}>
                    {selectedItem.total_revenue.toLocaleString()}
                    <span style={styles.revenueCurrency}>{selectedItem.currency}</span>
                  </div>
                </div>
                
              </div>
            </div>
            
            {/* Informations supplémentaires */}
            <div style={{ marginTop: '24px', color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
              {t('dataGeneratedOn')} {new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <ShoppingCart size={36} color="#7c3aed" />
          <h1 style={styles.title}>{t('salesStatus')}</h1>
        </div>
        
<div style={{ position: 'relative' }}>
  <button
    data-export-button
    style={styles.exportButton}
    onClick={exportData}
    disabled={loading || data.length === 0}
  >
    <Download size={16} />
    {t('export')}
  </button>
</div>
      </div>

      {/* Cartes de statistiques */}
      <div style={styles.statsGrid}>
        {/* Total des produits */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIconContainer, background: 'rgba(124, 58, 237, 0.1)' }}>
            <Package size={24} color="#7c3aed" />
          </div>
          <div style={styles.statInfo}>
            <div style={styles.statLabel}>{t('totalBookings')}</div>
            <div style={styles.statValue}>
              {stats.totalProducts.toLocaleString()}
            </div>
            <div style={styles.statSubtext}>
              {t('allBookings')}
            </div>
          </div>
        </div>

        {/* Réservations confirmées */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIconContainer, background: 'rgba(16, 185, 129, 0.1)' }}>
            <CheckCircle size={24} color="#10b981" />
          </div>
          <div style={styles.statInfo}>
            <div style={styles.statLabel}>{t('confirmedBookings')}</div>
            <div style={styles.statValue}>
              {stats.confirmedBookings.toLocaleString()}
            </div>
            <div style={styles.statSubtext}>
              {stats.totalProducts > 0 
                ? `${Math.round((stats.confirmedBookings / stats.totalProducts) * 100)}% ${t('ofTotal')}`
                : t('noData')
              }
            </div>
          </div>
        </div>

        {/* Réservations annulées */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIconContainer, background: 'rgba(239, 68, 68, 0.1)' }}>
            <XCircle size={24} color="#ef4444" />
          </div>
          <div style={styles.statInfo}>
            <div style={styles.statLabel}>{t('cancelledBookings')}</div>
            <div style={styles.statValue}>
              {stats.cancelledBookings.toLocaleString()}
            </div>
            <div style={styles.statSubtext}>
              {stats.totalProducts > 0 
                ? `${Math.round((stats.cancelledBookings / stats.totalProducts) * 100)}% ${t('ofTotal')}`
                : t('noData')
              }
            </div>
          </div>
        </div>

        {/* Services uniques */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIconContainer, background: 'rgba(245, 158, 11, 0.1)' }}>
            <BarChart3 size={24} color="#f59e0b" />
          </div>
          <div style={styles.statInfo}>
            <div style={styles.statLabel}>{t('uniqueServices')}</div>
            <div style={styles.statValue}>
              {stats.uniqueServices}
            </div>
            <div style={styles.statSubtext}>
              {t('differentServiceTypes')}
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={styles.filtersContainer}>
        <div style={styles.filtersTitle}>
          <Filter size={18} />
          {t('filters')}
        </div>
        
        <div style={styles.filtersGrid}>
          {/* Date de début */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <Calendar size={14} />
              {t('dateFrom')}
            </label>
            <input
              type="date"
              style={styles.input}
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </div>

          {/* Date de fin */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <Calendar size={14} />
              {t('dateTo')}
            </label>
            <input
              type="date"
              style={styles.input}
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>

          {/* Service */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('service')}</label>
            <select
              style={styles.select}
              value={filters.service}
              onChange={(e) => handleFilterChange('service', e.target.value)}
            >
              <option value="">{t('allServices')}</option>
              {services.map(service => (
                <option key={service} value={service}>
                  {t(service)}
                </option>
              ))}
            </select>
          </div>

          {/* Fournisseur */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('provider')}</label>
            {providersLoading ? (
              <div style={styles.loadingSelect}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                {t('loading')}...
              </div>
            ) : (
              <select
                style={styles.select}
                value={filters.provider_id}
                onChange={(e) => handleFilterChange('provider_id', e.target.value)}
              >
                <option value="">{t('allProviders')}</option>
                {providers.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name} ({provider.country_id})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Pays */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('country')}</label>
            {countriesLoading ? (
              <div style={styles.loadingSelect}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                {t('loading')}...
              </div>
            ) : (
              <select
                style={styles.select}
                value={filters.country_id}
                onChange={(e) => handleFilterChange('country_id', e.target.value)}
              >
                <option value="">{t('allCountries')}</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div style={styles.filterButtons}>
          <button
            style={styles.secondaryButton}
            onClick={resetFilters}
            disabled={loading}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#4b5563')}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#6b7280')}
          >
            <RefreshCw size={16} />
            {t('resetFilters')}
          </button>
          <button
            style={styles.primaryButton}
            onClick={applyFilters}
            disabled={loading}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#6d28d9')}
            onMouseLeave={(e) => !loading && (e.currentTarget.style.background = '#7c3aed')}
          >
            <Filter size={16} />
            {t('applyFilters')}
          </button>
        </div>
      </div>

      {/* Contenu principal */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
          {t('loadingData')}...
        </div>
      ) : data.length === 0 ? (
        <div style={styles.noDataContainer}>
          <Package size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ color: '#6b7280', marginBottom: '8px' }}>{t('noData')}</h3>
          <p style={{ color: '#9ca3af' }}>{t('noDataDescription')}</p>
        </div>
      ) : (
        <ReusableTable
          data={data}
          columns={columns}
          lang={lang}
          itemsPerPageOptions={[10, 25, 50, 100]}
          colors={{
            headerBg: '#7c3aed',
            buttonDetails: '#00a194ff'
          }}
          onRowClick={handleRowClick}
          onDetails={openDetailsModal}
        />
      )}

      {/* Modal de détails */}
      {showDetailsModal && renderDetailsModal()}

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default SalesStatusPage;